/*
 * Behind the scenes (sections/dte-backstage.liquid). Plain script, no library, about 3 KB minified.
 * From the approved prototype reports/design/2026-10-15-behind-the-scenes/proto-2.html.
 *
 * - The arrival, once, when the count bar comes into view: the bar fills to Today, then the signs swing up
 *   left to right.
 * - The count bar: a reading line follows the pointer (or a tap) and snaps to every five makers.
 * - Signs: tilt and light toward a mouse; a tap, click or Enter opens the full note; Escape closes.
 * - The road: Earlier/Later buttons, mouse drag, the front sign's post lights on phones, and the solid road
 *   turns dashed at Today.
 * - Opening position (Shane, 16 Oct): the start of the road, oldest photo first.
 * - Auto-advance (Shane, 16 Oct): every 5 s the road moves on two photos, and back to the start after the
 *   end. It waits while the road is off screen, the tab is hidden, a mouse is over it, focus is inside it, a
 *   note is open, or for 10 s after anyone scrolls, drags or presses Earlier/Later. A pause button stops it
 *   (WCAG 2.2.2).
 * Reduced motion: no arrival, no tilt, no auto-advance, the reading line jumps, scrolling is instant.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var wideMq = window.matchMedia('(min-width: 860px)');
  var escBound = false;

  function all(s, c) { return Array.prototype.slice.call(c.querySelectorAll(s)); }
  function f1(v) { return v.toFixed(1); }

  function init(sec) {
    if (sec.dataset.bsReady) return;
    var track = sec.querySelector('[data-bs-track]');
    var rail = sec.querySelector('[data-bs-rail]');
    var meter = sec.querySelector('[data-bs-meter]');
    if (!track || !rail || !meter) return;
    sec.dataset.bsReady = '1';
    sec.classList.toggle('is-reduced', reduce);

    var shown = parseInt(sec.getAttribute('data-shown'), 10) || 0;
    var goal = parseInt(sec.getAttribute('data-goal'), 10) || 100;
    var hint = sec.querySelector('[data-bs-hint]');
    if (fine && hint && hint.getAttribute('data-fine')) hint.textContent = hint.getAttribute('data-fine');

    var items = all('.dte-bs-item', track);
    var signs = all('[data-bs-sign]', track);
    var todayEl = track.querySelector('[data-bs-today]');
    function roadSplit() {
      if (todayEl) track.style.setProperty('--today-x', f1(todayEl.offsetLeft + todayEl.offsetWidth / 2) + 'px');
    }

    /* Mouse drag along the road (touch and trackpads scroll natively) */
    var dragging = false, dragMoved = false, dragX = 0, dragLeft = 0, userMoved = false;
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true; dragMoved = false; dragX = e.clientX; dragLeft = rail.scrollLeft;
    });
    rail.addEventListener('dragstart', function (e) { e.preventDefault(); });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - dragX;
      if (!dragMoved && Math.abs(dx) > 6) { dragMoved = true; userMoved = true; rail.classList.add('is-dragging'); }
      if (dragMoved) rail.scrollLeft = dragLeft - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('is-dragging');
      setTimeout(function () { dragMoved = false; }, 0);
    });

    /* Signs: open on tap, click or Enter; tilt and light toward a mouse */
    signs.forEach(function (s) {
      var btn = s.querySelector('button.dte-bs-sign__btn');
      if (btn) {
        btn.addEventListener('click', function () {
          if (dragMoved) return;
          var open = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          s.classList.toggle('is-open', open);
        });
      }
      if (!fine || reduce) return;
      var glare = s.querySelector('.dte-bs-sign__glare');
      s.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || dragging) return;
        var r = s.getBoundingClientRect(), nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
        s.classList.add('is-tracking');
        s.style.setProperty('--ry', f1((nx - 0.5) * 12) + 'deg');
        s.style.setProperty('--rx', f1((0.5 - ny) * 8) + 'deg');
        if (glare) {
          var fr = glare.parentNode.getBoundingClientRect();
          glare.style.setProperty('--gx', f1((e.clientX - fr.left) / fr.width * 100) + '%');
          glare.style.setProperty('--gy', f1((e.clientY - fr.top) / fr.height * 100) + '%');
        }
      });
      s.addEventListener('pointerleave', function () {
        s.classList.remove('is-tracking');
        s.style.setProperty('--rx', '0deg');
        s.style.setProperty('--ry', '0deg');
      });
    });
    if (!escBound) {
      escBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        all('.dte-bs-sign.is-open', document).forEach(function (s) {
          s.classList.remove('is-open');
          var b = s.querySelector('button.dte-bs-sign__btn');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      });
    }

    /* The count bar: a reading line that snaps to every five makers */
    var probe = meter.querySelector('[data-bs-probe]'), probeT = meter.querySelector('[data-bs-probe-t]'), probeHold = 0;
    var steps = Math.max(1, Math.round(goal / 5));
    function label(v) {
      if (v === 0) return meter.getAttribute('data-l-start');
      if (v <= shown) return meter.getAttribute('data-l-today');
      if (v >= goal) return meter.getAttribute('data-l-goal');
      return meter.getAttribute('data-l-ahead').replace('[n]', v).replace('[left]', v - shown);
    }
    function probeAt(clientX) {
      var r = meter.getBoundingClientRect();
      var f = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      var v = Math.round(f * steps) * 5;
      probe.style.setProperty('--x', f1(v / goal * r.width) + 'px');
      probe.classList.toggle('is-l', v <= goal * 0.1);
      probe.classList.toggle('is-r', v >= goal * 0.9);
      probeT.textContent = label(v);
      meter.classList.add('is-probing');
    }
    meter.addEventListener('pointermove', function (e) { if (e.pointerType === 'mouse') probeAt(e.clientX); });
    meter.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') meter.classList.remove('is-probing'); });
    meter.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      probeAt(e.clientX);
      clearTimeout(probeHold);
      probeHold = setTimeout(function () { meter.classList.remove('is-probing'); }, 2200);
    });

    /* The road: buttons and the front sign on phones */
    var prevBtn = sec.querySelector('[data-bs-prev]'), nextBtn = sec.querySelector('[data-bs-next]');
    function navState() {
      if (!prevBtn || !nextBtn) return;
      prevBtn.disabled = rail.scrollLeft < 4;
      nextBtn.disabled = rail.scrollLeft > rail.scrollWidth - rail.clientWidth - 4;
    }
    function page(dir) {
      userMoved = true;
      rail.scrollBy({ left: dir * Math.max(240, rail.clientWidth * 0.75), behavior: reduce ? 'auto' : 'smooth' });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { page(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { page(1); });
    var signItems = items.filter(function (it) { return it.classList.contains('dte-bs-item--sign'); });
    function front() {
      if (wideMq.matches) { signItems.forEach(function (it) { it.classList.remove('is-front'); }); return; }
      var rr = rail.getBoundingClientRect(), pad = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 16, best = null, bd = 1e9;
      signItems.forEach(function (it) { var d = Math.abs(it.getBoundingClientRect().left - (rr.left + pad)); if (d < bd) { bd = d; best = it; } });
      signItems.forEach(function (it) { it.classList.toggle('is-front', it === best); });
    }
    var frontT = 0;
    rail.addEventListener('scroll', function () { navState(); clearTimeout(frontT); frontT = setTimeout(front, 100); }, { passive: true });
    rail.addEventListener('touchstart', function () { userMoved = true; }, { passive: true });
    rail.addEventListener('wheel', function () { userMoved = true; }, { passive: true });
    rail.addEventListener('focusin', function () { userMoved = true; });

    /* Open at the start of the road, oldest photo first */
    function openAt() {
      if (userMoved) return;
      rail.scrollLeft = 0;
    }

    /* Auto-advance: two photos every 5 seconds, back to the start after the end */
    var playBtn = sec.querySelector('[data-bs-play]'), playT = sec.querySelector('[data-bs-play-t]');
    var auto = !reduce && signItems.length > 2;
    var paused = false, inView = false, hovering = false, focused = false, holdUntil = 0;
    function hold() { holdUntil = Date.now() + 10000; }
    function canRun() {
      return auto && !paused && inView && !hovering && !focused && !document.hidden && Date.now() >= holdUntil &&
        !track.querySelector('.dte-bs-sign.is-open') && !dragging;
    }
    function step() {
      if (!canRun()) return;
      var max = rail.scrollWidth - rail.clientWidth;
      if (rail.scrollLeft >= max - 4) { rail.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      var rr = rail.getBoundingClientRect(), pad = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 16, at = 0;
      for (var i = 0; i < signItems.length; i++) {
        if (signItems[i].getBoundingClientRect().left >= rr.left + pad - 8) { at = i; break; }
        at = i;
      }
      var to = signItems[Math.min(at + 2, signItems.length - 1)];
      var left = rail.scrollLeft + to.getBoundingClientRect().left - rr.left - pad;
      if (left <= rail.scrollLeft + 4) left = max; // last photos already in view: on to the end of the road
      rail.scrollTo({ left: Math.min(max, left), behavior: 'smooth' });
    }
    if (auto) {
      if (playBtn) {
        playBtn.hidden = false;
        playBtn.addEventListener('click', function () {
          paused = !paused;
          playBtn.classList.toggle('is-paused', paused);
          var l = playBtn.getAttribute(paused ? 'data-l-play' : 'data-l-pause');
          if (playT) playT.textContent = l;
        });
      }
      rail.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hovering = true; });
      rail.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hovering = false; });
      sec.addEventListener('focusin', function (e) { if (rail.contains(e.target)) focused = true; });
      sec.addEventListener('focusout', function (e) { if (rail.contains(e.target)) focused = false; });
      rail.addEventListener('touchstart', hold, { passive: true });
      rail.addEventListener('wheel', hold, { passive: true });
      rail.addEventListener('pointerdown', function (e) { if (e.pointerType === 'mouse') hold(); });
      if (prevBtn) prevBtn.addEventListener('click', hold);
      if (nextBtn) nextBtn.addEventListener('click', hold);
      // the 5 s clock restarts each time the road comes into view, so it never jumps the moment it arrives
      var tick = 0;
      function restart() { clearInterval(tick); tick = setInterval(step, 5000); }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          var was = inView;
          inView = es[es.length - 1].isIntersecting;
          if (inView && !was) restart();
        }, { threshold: 0.4 }).observe(rail);
      } else {
        inView = true;
        restart();
      }
    }

    /* The arrival: the bar fills to Today, then the signs go up left to right */
    var timers = [];
    function arrive() {
      timers.forEach(clearTimeout); timers = [];
      sec.classList.remove('is-going', 'is-up');
      sec.classList.add('is-armed');
      var railLeft = rail.getBoundingClientRect().left, vw = rail.clientWidth;
      items.forEach(function (it) {
        var x = it.getBoundingClientRect().left - railLeft;
        var d = x > vw ? 900 : 700 + Math.max(0, x) / vw * 700;
        it.style.setProperty('--d', Math.round(d) + 'ms');
      });
      void sec.offsetWidth;
      requestAnimationFrame(function () {
        sec.classList.add('is-going');
        timers.push(setTimeout(function () { sec.classList.add('is-up'); }, 20));
        timers.push(setTimeout(function () { sec.classList.remove('is-armed', 'is-up'); }, 2400));
      });
    }

    roadSplit();
    openAt();
    navState();
    front();
    if (!reduce && 'IntersectionObserver' in window) {
      var block = sec.querySelector('[data-bs-countblock]') || sec;
      var r = block.getBoundingClientRect();
      // Only arm if the bar has not been seen yet (a reload part-way down the page shows it as it is)
      if (r.top > window.innerHeight) {
        sec.classList.add('is-armed');
        var seen = new IntersectionObserver(function (es) {
          if (!es.some(function (e) { return e.isIntersecting; })) return;
          seen.disconnect();
          arrive();
        }, { threshold: 0.3 });
        seen.observe(block);
      }
    }
    window.addEventListener('resize', function () { roadSplit(); navState(); front(); });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { roadSplit(); openAt(); navState(); front(); });
    }
  }

  function start() { all('[data-dte-bs]', document).forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target && e.target.querySelector && e.target.querySelector('[data-dte-bs]');
    if (sec) init(sec);
  });
})();
